import {_} from 'lodash';
import * as ko from "knockout";
import {global, lm} from "../../../appContext";
import {
    IExportFlow,
    IExportTemplate,
    IHistory,
    IMessageData,
    IObjectData,
    IRouteDefinitionParams,
    IRuleset
} from "../../../index";
import {MAX_RESULTS_PER_PAGE} from "../../../constants";
import PromiseRequest from "../../../req/PromiseRequest";
import {AbstractExport} from "./AbstractExport";
import {createErrorMessage, getObjectDefinition, getRawSyncFieldValue} from "../../../util/misc";
import {nsFa} from "../../fa/fn";
import {getSearchObjectParams, xhrFactory} from "../../../util/request";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {nsObjectData} from "../../object/nsObjectData";
import {ISelectedListParams} from "../../object/selected-list";
import {IDialogOptionsComponent} from "../../dialog/index";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import {nsRoute} from "../../../util/route";
import Transform from "../mapping/Transform";
import {nsSearchToggle} from "../../search/nsSearchToggle";
import i = nsFa.i;
import fnPreviewRuleset = nsRuleset.fnPreviewRuleset;
import ButtonOrder = nsRoute.ButtonOrder;
import AllCheckbox = nsSearchToggle.AllCheckbox;
import ItemCheckbox = nsSearchToggle.ItemCheckbox;
import mappingHtml from './mapping.html?raw';

export let template: string = mappingHtml;

enum SelectionType {
    All,
    Selection,
    Ruleset,
}

export class viewModel<T = IExportFlow> extends AbstractExport {
    private readonly SelectionType = SelectionType;

    private exporttemplateid!: string;
    private paramMappingIds!: number[];
    private exportflows!: IExportFlow[];

    private _exporttemplate!: IExportTemplate;
    private objectTableDefinition!: nsObjectData.ObjectTableDefinition;
    private readonly reqPartialExport: PromiseRequest = new PromiseRequest("partialexport");
    private readonly reqSearchObject: PromiseRequest = global.getPromiseRequest('searchobject')
    private readonly requests: PromiseRequest[] = [];
    private readonly rulesetMessageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly selectedMappings: KnockoutObservableArray<IExportFlow> = ko.observableArray<IExportFlow>([]);
    private readonly selectedObjectIds: KnockoutObservableArray<number> = ko.observableArray<number>([]);
    private readonly selectedObjects: KnockoutObservableArray<IObjectData> = ko.observableArray<IObjectData>([]);
    private readonly selectedRuleset: KnockoutObservable<IRuleset | null> = ko.observable(null);
    private readonly selectionType: KnockoutObservable<SelectionType | null> = ko.observable(null);
    private showCheckboxes!: boolean;
    private allCheckbox!: AllCheckbox<IExportFlow>
    constructor(params: IRouteDefinitionParams) {
        super(params);

        this.pageButtons.add({
            onClick: this.onPreviewClick.bind(this),
            enabled: () => this.canPreview(),
            icon: 'fa-eye',
            label: 'preview',
            order: ButtonOrder.PREVIEW,
            validator: this.isReady,
        })
        this.pageButtons.add({
            onClick: this.onExecuteClick.bind(this),
            enabled: () => this.canExecute() && !this.isRequesting(),
            icon: 'fa-play',
            label: 'execute',
            order: ButtonOrder.SUBMIT,
            validator: this.isReady,
        })

        this.kosub.subscribe(this.selectionType, () => {
            this.showPreview(false);
            this.showResults(false);
        });
        this.kosub.subscribe(this.selectedObjects, () => {
            this.showPreview(false);
            this.showResults(false);
        });
        this.kosub.subscribe(this.selectionType as KnockoutSubscribable<SelectionType>, (type: SelectionType) => {
            if (type == SelectionType.Selection) {
                this.onSelectObjectsClick();
            }
        });
        this.kosub.subscribe(this.selectedRuleset as KnockoutSubscribable<IRuleset>, (ruleset: IRuleset) => {
            this.selectedObjectIds([]);
            this.reqSearchObject.abort();
            if (!ruleset || !this.__objectTypeId) {
                return;
            }
            this.rulesetMessageData({message: REQUEST_MESSAGE});
            this.reqSearchObject
                .request(getSearchObjectParams({
                    jruleset: JSON.stringify(ruleset),
                    objecttypeid: this.__objectTypeId,
                    onlyids: true,
                    resultsprpage: MAX_RESULTS_PER_PAGE,
                }))
                .then((data: number[]) => {
                    this.selectedObjectIds(data);
                    this.rulesetMessageData(null);
                })
                .catch((reason: string) => {
                    this.rulesetMessageData(createErrorMessage(String(reason)));
                });
        })
        this.initRequest();
    }

    protected initRequest() {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        this.exporttemplateid = this.__routeParams?.['exporttemplateid'] ?? '';
        this.paramMappingIds = this.__routeParams?.mappingid?.split(",").map((val: string) => parseInt(val)) ?? [];

        Promise.resolve()
            .then(() => {
                return this.__requestRouteData?.request()
                    .then((data: IExportTemplate[]) => {
                        const template = data[0];
                        if (!template) {
                            return;
                        }
                        this._exporttemplate = template;
                        this.__objectTypeId = this._exporttemplate.objecttype.id;
                        const objDef = getObjectDefinition(this.__objectTypeId);
                        if (objDef) {
                            this.__objDef = objDef;
                        }
                        if (!this.paramMappingIds?.length) {
                            this.paramMappingIds = this._exporttemplate.exportflow.filter(o => !o.inbound).map(o => o.id)
                        }
                        this.exportflows = this._exporttemplate.exportflow.filter((item: IExportFlow) => this.paramMappingIds.indexOf(item.id) > -1);
                        this.showCheckboxes = this.exportflows.length > 1
                        if (!this.showCheckboxes) {
                            this.selectedMappings(this.exportflows)
                        }

                        this.allCheckbox = new AllCheckbox({
                            data: this.exportflows,
                            selected: this.selectedMappings,
                            key: 'id',
                        })

                        this.messageData(null);
                        this.objectTableDefinition = new nsObjectData.ObjectTableDefinition(this.__objectTypeId);
                        this.isReady(true);
                    })
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    protected warnBeforeExecute(preview: boolean) {
        const strAction = `${preview ? "preview export" : "execute export"}`;
        global.dialogData({
            title: lm.t(strAction + `|suffix: [((attribute|ac))]`) ?? '',
            body: `<p>((${preview ? 'warning: preview export attribute|br' : 'warning: execute export attribute|br'}))</p><p>((do you wish to continue))}</p>`,
            confirmLabel: lm.t(`yes, continue`) ?? '',
            cancelLabel: lm.t(`no, cancel`) ?? '',
            onConfirm: this.execute.bind(this, preview),
        });
    }

    protected override execute(preview: boolean) {
        super.execute(preview);

        let objectids: string | null = null;
        switch (this.selectionType()) {
            case SelectionType.All:
                objectids = null;
                break;
            case SelectionType.Selection:
                objectids = this.selectedObjects().map(o => o.id).join(',');
                break;
            case SelectionType.Ruleset:
                objectids = this.selectedObjectIds().join(',');
                break;
            default:
                return;
        }

        this.reqPartialExport
            .request({
                exportflows: JSON.stringify({id: this.selectedMappings().map(o => o[this.allCheckbox.key as keyof IExportFlow])}),
                objectids: objectids,
                objecttypeid: this.__objectTypeId,
                test: preview,
            }, false, {
                timeout: 30 /*min*/ * 60 /*sec*/ * 1000 /*ms*/,
                xhr: xhrFactory(this.percentComplete as KnockoutObservable<number>, this.messageDataResult as KnockoutObservable<IMessageData>),
            })
            .then((data: IHistory[]) => {
                this.percentComplete(null);
                this.allTotal(data.length);
                this.exportActions(data);
                this.isDataReady(!!data.length);
                this.messageDataResult(data.length ? null : {message: 'no data'});
                this.isRequesting(false);
                if (!preview) {
                    const message = 'export completed';
                    global.snackbarData({message: message});
                }
            })
            .catch((reson: string) => {
                this.percentComplete(null);
                this.isDataReady(false);
                this.isRequesting(false);
                this.messageDataResult(createErrorMessage(reson))
            });
    }

    getSourceAttribute(attr: string): string | null {
        return attr ? this._exporttemplate.objecttype.name + "." + attr : null;
    }

    private onClickType(type: SelectionType) {
        this.selectionType(type);
        this.selectedObjectIds([]);
        switch (type) {
            case SelectionType.All:
                this.selectedObjects([]);
                this.selectedRuleset(null);
                break;
            case SelectionType.Selection:
                if (!this.selectedObjects().length) {
                    this.selectedObjects([]);
                }
                this.selectedRuleset(null);
                this.onSelectObjectsClick();
                break;
            case SelectionType.Ruleset:
                this.selectedObjects([]);
                break;
        }
    }

    private getCss(type: SelectionType) {
        return {active: this.selectionType() == type};
    }

    private onSelectObjectsClick() {
        const componentOptions: IDialogOptionsComponent = {
            content: {
                name: 'component/object/dialog/selectobjects',
                params: this.__getParams({
                    selectedObjects: this.selectedObjects,
                    __routeDef: {
                        objectTypeId: this.__objectTypeId,
                        showHeading: false,
                    }
                }),
            },
            title: `${i('fa-hand-point-up')} ${lm.t(`choose subject|s:((${this.__objDef!.name}|ac|p))`)}`,
        };
        global.dialogManager.addComponent(componentOptions);
    }

    private getSelectedListParams(): ISelectedListParams<IObjectData> {
        const params: ISelectedListParams<IObjectData> = {
            selectedObjects: this.selectedObjects,
            selectedObjectsSorted: this.selectedObjects,
            showHelp: false,
            table: this.objectTableDefinition.getTableDefinition(),
            getValue: (k, o) => getRawSyncFieldValue(k, o.syncfields),
        };
        return this.__getParams(_.defaultsDeep({
            __routeDef: {objectTypeId: this.__objectTypeId},
        }, params))
    }

    private canPreview(): boolean {
        return !this.showPreview() && this._hasSelection() && !!this.selectedMappings().length;
    }

    private canExecute() {
        return !this.showResults() && this._hasSelection() && !!this.selectedMappings().length;
    }

    private _hasSelection(): boolean {
        const selectionType = this.selectionType();
        const selectedObjects = this.selectedObjects();
        const selectedObjectIds = this.selectedObjectIds();
        switch (selectionType) {
            case SelectionType.All:
                return true;
            case SelectionType.Selection:
                return !!selectedObjects.length;
            case SelectionType.Ruleset:
                return !!selectedObjectIds.length;
            default:
                return false;
        }
    }

    private onPreviewRulesetClick() {
        const ruleset = this.selectedRuleset();
        if (ruleset) {
            fnPreviewRuleset(ruleset);
        }
    }

    private getExpression(expression: IExportFlow['expression']): string {
        if (expression.startsWith("[TRANSFORM;")) {
            return new Transform(expression).getOutput();
        }
        return expression;
    }

    private readonly allItemsChecked = ko.pureComputed(() => {
        return this.selectedMappings().length == this.exportflows.length
    })

    private getItemCheckbox(item: IExportFlow): ItemCheckbox<IExportFlow> {
        return new ItemCheckbox<IExportFlow>({
            item: item,
            key: this.allCheckbox.key,
            selected: this.selectedMappings,
        })
    }

    override dispose() {
        super.dispose();
        this.reqPartialExport.abort();
        this.reqSearchObject.abort();
        this.requests.forEach((req: PromiseRequest) => req.abort());
    }

}
